import type { Bounds, GameCoord } from "./coords.ts";
import { metersToUnits } from "./units.ts";

/**
 * Projection between game coordinates, world pixels and screen pixels.
 *
 * Three spaces, kept strictly apart:
 *
 *   game   - what the player reads off the in-game map. +y is north.
 *   world  - the tile pyramid at native resolution, origin top-left, +y down.
 *   screen - the viewport, after the camera's pan and zoom.
 *
 * The y flip lives here and nowhere else. Every bug in a map tool of this kind
 * eventually turns out to be a second, contradictory y flip somewhere further
 * up, so this module is the only code allowed to know which way is up.
 */

export interface Camera {
  /** Screen pixels per world pixel. */
  readonly scale: number;
  /** Screen position of the world origin, in pixels. */
  readonly x: number;
  readonly y: number;
}

export interface Viewport {
  readonly width: number;
  readonly height: number;
}

export interface ProjectionConfig {
  /** Game-coordinate extent covered by the tile pyramid. */
  readonly tileBounds: Bounds;
  /** Native size of the pyramid in world pixels (square). */
  readonly worldSize: number;
}

export interface ScreenPoint {
  readonly x: number;
  readonly y: number;
}

/** Game coordinate to world pixel. */
export function gameToWorld(point: GameCoord, config: ProjectionConfig): ScreenPoint {
  const { tileBounds, worldSize } = config;
  const spanX = tileBounds.maxX - tileBounds.minX;
  const spanY = tileBounds.maxY - tileBounds.minY;
  return {
    x: ((point.x - tileBounds.minX) / spanX) * worldSize,
    // North is up in game space but down in pixel space, hence the flip.
    y: ((tileBounds.maxY - point.y) / spanY) * worldSize,
  };
}

/** World pixel to game coordinate. */
export function worldToGame(point: ScreenPoint, config: ProjectionConfig): GameCoord {
  const { tileBounds, worldSize } = config;
  const spanX = tileBounds.maxX - tileBounds.minX;
  const spanY = tileBounds.maxY - tileBounds.minY;
  return {
    x: tileBounds.minX + (point.x / worldSize) * spanX,
    y: tileBounds.maxY - (point.y / worldSize) * spanY,
  };
}

/** World pixel to screen pixel, under a camera. */
export function worldToScreen(point: ScreenPoint, camera: Camera): ScreenPoint {
  return {
    x: point.x * camera.scale + camera.x,
    y: point.y * camera.scale + camera.y,
  };
}

/** Screen pixel to world pixel, under a camera. */
export function screenToWorld(point: ScreenPoint, camera: Camera): ScreenPoint {
  return {
    x: (point.x - camera.x) / camera.scale,
    y: (point.y - camera.y) / camera.scale,
  };
}

/** Game coordinate straight to screen pixel. */
export function gameToScreen(
  point: GameCoord,
  camera: Camera,
  config: ProjectionConfig,
): ScreenPoint {
  return worldToScreen(gameToWorld(point, config), camera);
}

/** Screen pixel straight to game coordinate. */
export function screenToGame(
  point: ScreenPoint,
  camera: Camera,
  config: ProjectionConfig,
): GameCoord {
  return worldToGame(screenToWorld(point, camera), config);
}

/**
 * Screen-space radius for a real-world distance centred on a game
 * coordinate, e.g. a weapon's range ring.
 *
 * Projects both ends of the radius through the same `gameToScreen` pipeline
 * as every point on the map, rather than deriving a second, independent
 * pixels-per-meter factor — which is exactly the kind of thing that quietly
 * stops matching the tiles under it the day this module's math changes and
 * a hand-rolled copy elsewhere does not.
 */
export function metersToScreenRadius(
  meters: number,
  center: GameCoord,
  camera: Camera,
  config: ProjectionConfig,
): number {
  const centerScreen = gameToScreen(center, camera, config);
  const edgeScreen = gameToScreen(
    { x: center.x + metersToUnits(meters), y: center.y },
    camera,
    config,
  );
  return Math.abs(edgeScreen.x - centerScreen.x);
}

/**
 * Zoom around a fixed screen point, so the spot under the cursor stays put.
 * Anything else feels like the map is sliding away from you.
 */
export function zoomAround(
  camera: Camera,
  focus: ScreenPoint,
  nextScale: number,
  minScale: number,
  maxScale: number,
): Camera {
  const clamped = Math.min(maxScale, Math.max(minScale, nextScale));
  const world = screenToWorld(focus, camera);
  return {
    scale: clamped,
    x: focus.x - world.x * clamped,
    y: focus.y - world.y * clamped,
  };
}

/** Camera that fits a game-coordinate rectangle into the viewport. */
export function fitBounds(
  bounds: Bounds,
  viewport: Viewport,
  config: ProjectionConfig,
  padding = 0.96,
): Camera {
  const topLeft = gameToWorld({ x: bounds.minX, y: bounds.maxY }, config);
  const bottomRight = gameToWorld({ x: bounds.maxX, y: bounds.minY }, config);

  const width = Math.abs(bottomRight.x - topLeft.x);
  const height = Math.abs(bottomRight.y - topLeft.y);
  if (width === 0 || height === 0 || viewport.width === 0 || viewport.height === 0) {
    return { scale: 1, x: 0, y: 0 };
  }

  const scale = Math.min(viewport.width / width, viewport.height / height) * padding;
  const centerWorld = {
    x: (topLeft.x + bottomRight.x) / 2,
    y: (topLeft.y + bottomRight.y) / 2,
  };

  return {
    scale,
    x: viewport.width / 2 - centerWorld.x * scale,
    y: viewport.height / 2 - centerWorld.y * scale,
  };
}

/** Camera centred on a game coordinate at a given scale. */
export function centerOn(
  point: GameCoord,
  scale: number,
  viewport: Viewport,
  config: ProjectionConfig,
): Camera {
  const world = gameToWorld(point, config);
  return {
    scale,
    x: viewport.width / 2 - world.x * scale,
    y: viewport.height / 2 - world.y * scale,
  };
}

/**
 * Pick the tile pyramid level whose native resolution best matches the current
 * zoom. Rounding up one level early trades a little bandwidth for never
 * showing a visibly soft map.
 */
export function tileZoomFor(
  camera: Camera,
  maxZoom: number,
  minZoom: number,
  tileSize: number,
  worldSize: number,
): number {
  const nativeTiles = worldSize / tileSize;
  const ideal = Math.log2(nativeTiles * camera.scale);
  return Math.min(maxZoom, Math.max(minZoom, Math.ceil(ideal)));
}

export interface VisibleTile {
  readonly x: number;
  readonly y: number;
  readonly zoom: number;
  /** Screen rectangle this tile occupies. */
  readonly left: number;
  readonly top: number;
  readonly size: number;
}

/**
 * Every tile touching the viewport, at the given pyramid level.
 *
 * `overscan` pulls in a ring of extra tiles beyond the edges so panning does
 * not reveal blank space before the next image has loaded.
 */
export function visibleTiles(
  camera: Camera,
  viewport: Viewport,
  zoom: number,
  worldSize: number,
  overscan = 1,
): VisibleTile[] {
  const tilesPerSide = 2 ** zoom;
  const tileWorldSize = worldSize / tilesPerSide;
  const tileScreenSize = tileWorldSize * camera.scale;
  if (tileScreenSize <= 0) return [];

  const topLeft = screenToWorld({ x: 0, y: 0 }, camera);
  const bottomRight = screenToWorld({ x: viewport.width, y: viewport.height }, camera);

  const minTileX = Math.floor(topLeft.x / tileWorldSize) - overscan;
  const maxTileX = Math.floor(bottomRight.x / tileWorldSize) + overscan;
  const minTileY = Math.floor(topLeft.y / tileWorldSize) - overscan;
  const maxTileY = Math.floor(bottomRight.y / tileWorldSize) + overscan;

  const tiles: VisibleTile[] = [];
  for (let ty = minTileY; ty <= maxTileY; ty++) {
    if (ty < 0 || ty >= tilesPerSide) continue;
    for (let tx = minTileX; tx <= maxTileX; tx++) {
      if (tx < 0 || tx >= tilesPerSide) continue;
      tiles.push({
        x: tx,
        y: ty,
        zoom,
        left: tx * tileScreenSize + camera.x,
        top: ty * tileScreenSize + camera.y,
        // A hair of overlap hides the sub-pixel seams between adjacent tiles.
        size: tileScreenSize + 1,
      });
    }
  }
  return tiles;
}
